import { Dialog, DialogContent, Grid } from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import {
  FeedGroupInput,
  OperationNames,
  SortEnumType,
  useAddPersonalFeedTasksMutation,
  useGetArraySystemAutomationMethodsQuery,
  useGetFeedGroupsLazyQuery,
  useGetGroupPersonalTasksLazyQuery,
  useGetPlatesNoPagingQuery,
  useSaveFeedGroupsMutation,
} from "graphql/generated/schema-types";
import { DeepCopy } from "helpers/object-helpers";
import { isEmpty, isEqual } from "lodash";
import moment from "moment";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Alert, AlertType } from "shared-components/toast";
import { useConfirm } from "state-provider/ConfirmDialogProvider/hooks";
import * as Yup from "yup";
import {
  AddFeedTaskProps,
  blankFeedGroupRow,
  feedDayToday,
  feedDayTomorrow,
  FeedGroupErrors,
  formatAndConvertToEst,
  renderLoadingOrNoFeedGroups,
  roundedCurrentTime,
  setDatesToTomorrow,
  validators,
} from "./AddFeedTask.Functions";
import {
  StyledForm,
  StyledFormControl,
} from "./AddFeedTask/AddFeedTask.Styled";
import { FeedGroupFooter } from "./AddFeedTask/FeedGroupFooter";
import { FeedGroupFormRow } from "./AddFeedTask/FeedGroupFormRow";
import { FeedGroupHeader } from "./AddFeedTask/FeedGroupHeader";

export const AddFeedTask = ({ isOpen, handleClose }: AddFeedTaskProps) => {
  const [getFeedGroups, { loading, data: feedGroupData }] =
    useGetFeedGroupsLazyQuery();
  const [getGroupPersonalTasks, { data: personalTaskData }] =
    useGetGroupPersonalTasksLazyQuery();
  const [saveFeedGroups] = useSaveFeedGroupsMutation();
  const [addPersonalFeedTasks] = useAddPersonalFeedTasksMutation();
  const asamQuery = useGetArraySystemAutomationMethodsQuery();
  const [feedGroupCollapseOpen, setFeedGroupCollapseOpen] = useState<
    number | null
  >(null);
  const [groupId, setGroupId] = useState<number | undefined | null>(0);
  const [feedDay, setFeedDay] = useState(feedDayToday);
  const [todayTasks, setTodayTasks] = useState(new Array<number>());
  const [tomorrowTasks, setTomorrowTasks] = useState(new Array<number>());
  const [feedGroupsToDelete, setFeedGroupsToDelete] = useState(
    new Array<FeedGroupInput>(),
  );
  const [deselectedFeedGroupIds, setDeselectedFeedGroupIds] = useState<
    Set<number>
  >(new Set());
  const [isCreatingTasks, setIsCreatingTasks] = useState(false);

  useEffect(() => {
    if (!groupId) return;

    getFeedGroups({ variables: { groupId: groupId! } });

    getGroupPersonalTasks({
      variables: { groupId: groupId!, today: roundedCurrentTime() },
    });
    setFeedGroupsToDelete(new Array<FeedGroupInput>());
    setDeselectedFeedGroupIds(new Set());
  }, [groupId]);

  const { isConfirmed } = useConfirm();

  const asams = asamQuery?.data?.arraySystemAutomationMethods;

  const getPlates = useGetPlatesNoPagingQuery({
    variables: {
      where: {
        isActive: { eq: true },
        arrayPlateType: {
          arrayPlateRoleId: { in: [1, 2, 10, 11, 12, 13, 18] },
        },
      },
      order: { plateBarcode: SortEnumType.Asc },
    },
  });

  const plates = getPlates.data?.platesNoPaging ?? [];

  const allFeedGroups = useMemo(
    () => feedGroupData?.feedGroups,
    [feedGroupData],
  );
  const feedGroupArray: FeedGroupInput[] =
    allFeedGroups?.map((fg) => {
      const fgMethod =
        fg?.arraySystemAutomationMethod?.automationMethod?.lookupArrayMethods;
      const fgMethodId = fgMethod && fgMethod[0]?.methodId;

      return {
        feedGroupId: fg?.feedGroupId ?? 0,
        groupId: fg?.groupId,
        arraySystemAutomationMethodId: fg?.arraySystemAutomationMethodId,
        feedGroupName: fg?.feedGroupName,
        isActive: fg?.isActive ?? true,
        startTimeScheduled:
          formatAndConvertToEst(fg?.startTimeScheduled) ??
          roundedCurrentTime().clone().format(),
        numberOfPlates: fg?.numberOfPlates,
        methodId: fgMethodId,
        arraySystemId: fg?.arraySystemAutomationMethod?.arraySystemId,
        automationMethodId: fg?.arraySystemAutomationMethod?.automationMethodId,
        plateIds:
          fg?.feedGroupPlates
            ?.map((item) => item?.plateId)
            ?.filter((id): id is number => typeof id === "number") ?? [],
        generateMystRack: fg?.generateMystRack ?? false,
      };
    }) ?? [];

  const createdFeedTasks = useMemo(
    () => personalTaskData?.groupPersonalTasks,
    [personalTaskData],
  );

  const saveCreatedTasksToState = () => {
    const feedGroupIdsToday: number[] = [];
    const feedGroupIdsTomorrow: number[] = [];

    const dayOfMonthToday = moment().date();
    const dayOfMonthTomorrow = moment().add(1, "days").date();

    if (!createdFeedTasks) return;

    createdFeedTasks.forEach((persTask) => {
      const localStartTimeScheduled = new Date(
        persTask?.startTimeScheduled as string,
      );
      const dayOfStartTimeScheduled = moment(localStartTimeScheduled).date();

      if (dayOfStartTimeScheduled === dayOfMonthToday) {
        feedGroupIdsToday.push(persTask?.feedGroupId ?? -1);
      } else if (dayOfStartTimeScheduled === dayOfMonthTomorrow) {
        feedGroupIdsTomorrow.push(persTask?.feedGroupId ?? -1);
      }
    });

    setTodayTasks(feedGroupIdsToday);
    setTomorrowTasks(feedGroupIdsTomorrow);
  };

  useEffect(() => {
    saveCreatedTasksToState();
  }, [createdFeedTasks]);

  const isTaskCreated = (feedGroupId: number) => {
    if (
      feedDay === feedDayToday &&
      (todayTasks.includes(feedGroupId) || tomorrowTasks.includes(feedGroupId))
    ) {
      return true;
    }

    if (feedDay === feedDayTomorrow && tomorrowTasks.includes(feedGroupId)) {
      return true;
    }

    return false;
  };

  const isFeedGroupSelected = (feedGroupId: number | null | undefined) =>
    typeof feedGroupId === "number" && !deselectedFeedGroupIds.has(feedGroupId);

  const toggleFeedGroupSelected = (feedGroupId: number) => {
    setDeselectedFeedGroupIds((previous) => {
      const next = new Set(previous);

      if (next.has(feedGroupId)) next.delete(feedGroupId);
      else next.add(feedGroupId);

      return next;
    });
  };

  const selectableFeedGroups = () =>
    formValues.feedGroupArray.filter(
      (feedGroup) =>
        typeof feedGroup.feedGroupId === "number" &&
        !isTaskCreated(feedGroup.feedGroupId),
    );

  const setAllFeedGroupsSelected = (selected: boolean) => {
    const selectableIds = selectableFeedGroups()
      .map((feedGroup) => feedGroup.feedGroupId)
      .filter((id): id is number => typeof id === "number");

    setDeselectedFeedGroupIds((previous) => {
      const next = new Set(previous);

      selectableIds.forEach((id) =>
        selected ? next.delete(id) : next.add(id),
      );

      return next;
    });
  };

  const feedGroupsToCreate = () =>
    selectableFeedGroups().filter((feedGroup) =>
      isFeedGroupSelected(feedGroup.feedGroupId),
    );

  const selectedFeedGroupsAreUnsaved = () =>
    feedGroupsToCreate().some((feedGroup) => {
      const savedFeedGroup = formik.initialValues.feedGroupArray.find(
        (initialFeedGroup) =>
          initialFeedGroup.feedGroupId === feedGroup.feedGroupId,
      );

      return !savedFeedGroup || !isEqual(savedFeedGroup, feedGroup);
    });

  const selectedFeedGroupsHaveErrors = () =>
    formValues.feedGroupArray.some(
      (feedGroup, index) =>
        !isTaskCreated(feedGroup.feedGroupId!) &&
        isFeedGroupSelected(feedGroup.feedGroupId) &&
        !isEmpty(errorMessageObj(index)),
    );

  const cleanupForm = () => {
    Alert({ type: AlertType.SUCCESS, message: "Feed Task(s) Created" });
    setFeedDay(feedDayToday);
    handleClose();
  };

  const createPersonalFeedTasks = async () => {
    const selectedFeedGroups = DeepCopy(feedGroupsToCreate());

    if (isEmpty(selectedFeedGroups)) return;

    const currentErrors = (await formik.validateForm()) as FeedGroupErrors;
    const selectedFeedGroupIsInvalid = formValues.feedGroupArray.some(
      (feedGroup, index) =>
        !isTaskCreated(feedGroup.feedGroupId!) &&
        isFeedGroupSelected(feedGroup.feedGroupId) &&
        !isEmpty(currentErrors?.feedGroupArray?.[index]),
    );

    if (selectedFeedGroupIsInvalid) {
      Alert({
        type: AlertType.ERROR,
        message: "Fix the selected Feed Group(s) before creating tasks",
      });
      return;
    }

    const dayAlreadyHasFeedTasks = formValues.feedGroupArray.some((feedGroup) =>
      isTaskCreated(feedGroup.feedGroupId!),
    );

    if (feedDay === feedDayToday && dayAlreadyHasFeedTasks) {
      const confirmed = await isConfirmed(
        "Are you sure you want to create additional Feed Task(s) for today?",
      );

      if (!confirmed) {
        return;
      }
    }
    setIsCreatingTasks(true);

    try {
      const res = await addPersonalFeedTasks({
        variables: {
          inputs:
            feedDay === feedDayToday
              ? selectedFeedGroups
              : setDatesToTomorrow(selectedFeedGroups),
        },
        refetchQueries: [
          OperationNames.Query.getCalendarNodes,
          OperationNames.Query.getGroupPersonalTasks,
        ],
      });

      if (res.data?.addPersonalFeedTasks === true) {
        cleanupForm();
      }
    } finally {
      setIsCreatingTasks(false);
    }
  };

  const handleFeedTaskSubmit = (event: FormEvent) => {
    event.preventDefault();
    createPersonalFeedTasks();
  };

  const formik = useFormik({
    initialValues: { feedGroupArray },
    enableReinitialize: true,
    onSubmit: createPersonalFeedTasks,
    validationSchema: Yup.object().shape(validators),
  });

  const errors = formik.errors;
  const formValues = formik.values;
  const isDirty = formik.dirty;
  const handleBlur = formik.handleBlur;
  const handleChange = formik.handleChange;
  const setFieldValue = formik.setFieldValue;
  const setValues = formik.setValues;

  const createFeedGroupRow = () => {
    const currentFeedGroups = DeepCopy(formValues.feedGroupArray);

    currentFeedGroups.push(blankFeedGroupRow(groupId));

    setValues({ feedGroupArray: currentFeedGroups });
  };

  const saveFeedGroupsHandler = () => {
    const activeFeedGroups = DeepCopy(formValues.feedGroupArray);
    const deletedFeedGroups = DeepCopy(feedGroupsToDelete);
    const activeAndDeletedFeedGroups = [
      ...activeFeedGroups,
      ...deletedFeedGroups,
    ];
    setFeedGroupsToDelete(new Array<FeedGroupInput>());
    saveFeedGroups({
      variables: {
        inputs: activeAndDeletedFeedGroups,
      },
      refetchQueries: [OperationNames.Query.getFeedGroups],
    });
  };

  const errorMessageObj = (index: number) => {
    return (errors as FeedGroupErrors)?.feedGroupArray?.[index];
  };

  const renderFeedGroupFormRows = (
    deleteRowHelper: (index: number) => void,
  ) => {
    return formValues.feedGroupArray.map((fg, index) => {
      const toggleDisabled = isTaskCreated(fg.feedGroupId!);

      return (
        <FeedGroupFormRow
          key={fg.feedGroupId ?? `new-${index}`}
          isSelected={isFeedGroupSelected(fg.feedGroupId)}
          toggleSelected={toggleFeedGroupSelected}
          asams={asams}
          deleteRowHelper={deleteRowHelper}
          errorMessageObj={errorMessageObj}
          feedGroupsToDelete={feedGroupsToDelete}
          formValues={formValues}
          handleBlur={handleBlur}
          handleChange={handleChange}
          index={index}
          setFeedGroupsToDelete={setFeedGroupsToDelete}
          setFieldValue={setFieldValue}
          toggleDisabled={toggleDisabled}
          feedGroupCollapseOpen={feedGroupCollapseOpen}
          setFeedGroupCollapseOpen={setFeedGroupCollapseOpen}
          plates={plates}
        />
      );
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth={true} maxWidth="lg">
      <DialogContent>
        <FeedGroupHeader
          createFeedGroupRow={createFeedGroupRow}
          feedDay={feedDay}
          groupId={groupId}
          setFeedDay={setFeedDay}
          setFieldValue={setFieldValue}
          setGroupId={setGroupId}
          selectedCount={feedGroupsToCreate().length}
          selectableCount={selectableFeedGroups().length}
          setAllFeedGroupsSelected={setAllFeedGroupsSelected}
        />
        {renderLoadingOrNoFeedGroups(
          loading,
          isEmpty(formValues.feedGroupArray),
        )}
        <FormikProvider value={formik}>
          <StyledForm onSubmit={handleFeedTaskSubmit}>
            <StyledFormControl>
              <FieldArray
                name="feedGroupArray"
                render={({ remove: deleteRowHelper }) => (
                  <Grid container rowGap={2} sx={{ ml: 1, mb: 1 }}>
                    {renderFeedGroupFormRows(deleteRowHelper)}
                  </Grid>
                )}
              />
              <FeedGroupFooter
                nothingSelected={isEmpty(feedGroupsToCreate())}
                errorsPresent={!isEmpty(errors)}
                selectedErrorsPresent={selectedFeedGroupsHaveErrors()}
                handleClose={handleClose}
                isDirty={isDirty}
                selectedUnsaved={selectedFeedGroupsAreUnsaved()}
                isSubmitting={isCreatingTasks}
                saveFeedGroupsHandler={saveFeedGroupsHandler}
              />
            </StyledFormControl>
          </StyledForm>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
};
